import { LocaleSettings } from './locale.settings';
import { z } from 'zod';

export class CurrencyFormatter {

    public static formatCurrency(amount: number = 0, rounding: number = 2): string {
        // Normalize -0 to 0
        if (Object.is(amount, -0)) {
            amount = 0;
        }

        // Ensure rounding is a safe non-negative integer
        if (!Number.isInteger(rounding) || rounding < 0) {
            rounding = 2;
        }

        const currency = new Intl
            .NumberFormat('en', {
                style: 'currency',
                currency: LocaleSettings.currencyUnit,
                maximumFractionDigits: rounding
            })
            .format(amount);

        return currency;
    }

    /**
     * Validates currency format with optional currency symbol and comma separators
     * Valid formats:
     * - With currency + commas: $1,000, $20,000,000.12345
     * - With currency, no commas: $1000, $20000000.12345
     * - No currency + commas: 1,000, 20,000,000.12345
     * - No currency, no commas: 1000, 20000000.12345
     * @param priceText - The price string to validate
     * @returns true if valid, false if invalid, true if empty
     */
    public static isValidImportingUnitPrice(priceText: string): boolean {
        if (!priceText) return true;

        const currencySymbol = LocaleSettings.currencySymbol;

        // Escape $ symbol for regex ($ is the only currency symbol that's a regex special character)
        // Examples: $ → \$, £ → £, € → €, R$ → R\$
        const escapedSymbol = currencySymbol.replace(/\$/g, '\\$');

        // Create dynamic currency schema based on current currency symbol
        // Pattern supports four formats:
        // 1. With currency + commas: $20,000,000.12
        // 2. With currency, no commas: $20000000.12
        // 3. No currency + commas: 20,000,000.12
        // 4. No currency, no commas: 20000000.12
        // - Currency symbol is optional
        // - Numbers can have comma separators (1-3 digits, then groups of comma + 3 digits) OR no commas
        // - Optional decimal point followed by one or more digits
        const currencyPattern = new RegExp(
            `^${escapedSymbol}?(\\d{1,3}(,\\d{3})*|\\d+)(\\.\\d+)?$`
        );

        const currencySchema = z.string().regex(
            currencyPattern,
            `Invalid currency format. Expected: ${currencySymbol}20,000.12345 or 20,000.12345`
        );

        // Validate format using Zod schema
        const validationResult = currencySchema.safeParse(priceText);
        if (!validationResult.success) {
            return false;
        }

        // Extract numeric value (remove currency symbol and commas)
        const priceString = priceText.replace(/[^0-9.]/g, '');
        const price = Number(priceString);
        const isNumber = typeof price === 'number' && Number.isFinite(price);

        if (!isNumber) {
            return false;
        }

        return true;
    }

    public static formatImportingUnitPrice(priceText): number {
        const isValidImportingUnitPrice = this.isValidImportingUnitPrice(priceText);
        if (isValidImportingUnitPrice) {
            const priceString = priceText.replace(/[^0-9.]/g, '');
            return Number(priceString);
        }

        return 0;
    }
}